import type { AnalyticsConfig } from "@website/types";
import { type BookmarkButton } from "./bookmarkbutton";
import { type BreakingNewsEvent } from "./breaking-news";
import {
  BOOKMARKBUTTON_TOGGLE_EVENT,
  BOOKMARKLIST_LOADED_EVENT,
  BREAKING_NEWS_GTM_EVENT,
  GALLERY_CHANGE_EVENT,
  GTM_INIT_EVENT,
  MESSAGE_HANDLER_INTERACTION_EVENT,
  ONBOARDINGPOPUP_CLOSED_EVENT,
  ONBOARDINGPOPUP_SHOWN_EVENT,
  PAYWALL_CLICKED_EVENT,
  TEXTTOSPEECH_CLOSED_EVENT,
  TEXTTOSPEECH_OPENED_EVENT,
  ZEPHR_DECISIONS_FINISHED_EVENT,
} from "./events.js";
import { ChangeEvent } from "./gallery";
import { netidEventsArray, StatusEvent } from "./netid";
import { type PaywallControl } from "./paywallcontrol";
import { getOrientation, getViewport } from "./utils";

type EventData = {
  event: string;
  [key: string]: unknown;
};

const wsPaywallReady = window.customElements.whenDefined("ws-paywall");
const wsLinkReady = window.customElements.whenDefined("ws-link");

/**
 * This web component, GoogleTagManager, is a custom HTML element designed
 * to integrate Google Tag Manager (GTM) into a web page.
 * @see  https://support.google.com/tagmanager/answer/14847097
 */
export class GoogleTagManager extends HTMLElement {
  #teaserElements: NodeListOf<HTMLElement> | null = null;
  #breakingNewsElement: HTMLElement | null = null;

  get authToken(): string {
    return this.getAttribute("auth-token") ?? "";
  }

  get containerId(): string {
    return this.getAttribute("container-id") ?? "";
  }

  get envCode(): string {
    return this.getAttribute("env-code") ?? "";
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  #ctaListenerAttached = false;

  connectedCallback() {
    try {
      this.#initialize(this.#defaultPageConfig());

      //Add eventlisteners for virtual pageview in galleries
      const galleries = document.querySelectorAll(
        "ws-gallery, ws-gallery-embed, ws-gallery-index, ws-gallery-lead",
      );
      galleries.forEach((gallery) => {
        gallery.addEventListener(
          GALLERY_CHANGE_EVENT,
          this.#handleVirtualPageviewGallery,
        );
        gallery.addEventListener(
          GALLERY_CHANGE_EVENT,
          this.#handleSwipeGallery,
        );
      });

      //Add eventlisteners for virtual pageview in messagehandler
      const messagehandlers = document.querySelectorAll("ws-messagehandler");
      messagehandlers.forEach((messagehandler) => {
        messagehandler.addEventListener(
          MESSAGE_HANDLER_INTERACTION_EVENT,
          this.#handleVirtualPageviewMessagehandler,
        );
      });

      //Add eventlisteners for TTS
      const ttsHandlers = document.querySelectorAll("ws-texttospeech");
      ttsHandlers.forEach((ttsHandler) => {
        ttsHandler.addEventListener(
          TEXTTOSPEECH_OPENED_EVENT,
          this.#handleTtsOpened,
        );
        ttsHandler.addEventListener(
          TEXTTOSPEECH_CLOSED_EVENT,
          this.#handleTtsClosed,
        );
      });

      //Add eventlistener for NetID
      const netid = document.querySelector("ws-netid");
      netidEventsArray.forEach((entry) => {
        netid?.addEventListener(entry, this.#handleNetidEvent);
      });
    } catch (error) {
      if (error instanceof SyntaxError) {
        console.error("Error parsing Google Tag Manager configuration:", error);
      } else {
        console.error("An unexpected error occurred:", error);
      }
    }
    this.#bindTeasers();
    this.#bindBookmarkButtons();
    this.#bindCtaButtons();
    this.#bindOnboardingPopup();
    const zephrElement = document.getElementsByTagName("ws-zephr");
    if (zephrElement.length !== 0) {
      this.#bindPaywall();
    } else {
      this.#paywallClickedListener();
      this.#paywallLoginClickedListener();
      this.#handlePaywallShown();
      this.#handleInlinePaywallVisibility();
    }
    this.#handleZephrOutcome();
    this.#bindLogoraBarrier();
    document.addEventListener(
      BREAKING_NEWS_GTM_EVENT,
      this.#handleBreakingNewsEvent,
    );
  }

  #executeScripts() {
    const gtmExtendedParams =
      this.authToken != "" && this.envCode != ""
        ? `&gtm_auth=${this.authToken}&gtm_preview=${this.envCode}&gtm_cookies_win=x`
        : "";

    const script = document.createElement("script");
    script.textContent = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl${gtmExtendedParams ? `+'${gtmExtendedParams}'` : ""};f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${this.containerId}');`;
    this.shadowRoot!.appendChild(script);
  }

  #defaultPageConfig(): AnalyticsConfig {
    const configScript = this.querySelector(`script[type="application/json"]`);

    if (!configScript) {
      console.warn("Google Tag Manager configuration script not found.");
      return this.#defaultConfig(); // Return a basic default config
    }

    try {
      const config = JSON.parse(configScript.textContent ?? "{}");
      return this.#validateConfig(config) ? config : this.#defaultConfig();
    } catch (error) {
      console.error("Error parsing Google Tag Manager configuration:", error);
      return this.#defaultConfig(); // Fallback to default config
    }
  }

  // Define a basic fallback configuration
  #defaultConfig() {
    return {
      content: {},
      tech: {},
      login: {},
    } as AnalyticsConfig;
  }

  // Validation method for required structure or fields
  #validateConfig(config: AnalyticsConfig): boolean {
    // Simple check for example purposes; expand as needed
    return (
      config &&
      typeof config === "object" &&
      "content" in config &&
      "tech" in config
    );
  }

  #handleNetidEvent = (e: Event) => {
    const event = e as StatusEvent;
    const details = event.detail
      ? { [event.detail.eventAction]: event.detail.eventLabel }
      : null;
    window.dataLayer.push({
      event: event.type,
      ...details,
    });
  };

  #handleTtsOpened = () => {
    const currentConfig = this.#defaultPageConfig();
    const ttsConfig = {
      ...currentConfig,
      event: "tts_opened",
    };
    window.dataLayer.push(ttsConfig);
  };

  #handleTtsClosed = () => {
    const currentConfig = this.#defaultPageConfig();
    const ttsConfig = {
      ...currentConfig,
      event: "tts_closed",
    };
    window.dataLayer.push(ttsConfig);
  };

  #handleVirtualPageviewGallery = (e: Event) => {
    const event = e as ChangeEvent;
    const { galleryHeadline, itemNumber, itemUrl, totalItems } = event.detail;

    const currentConfig = this.#defaultPageConfig();
    const galleryConfig = {
      ...currentConfig,
      content: {
        ...currentConfig.content,
        virt_path_url: itemUrl,
      },
      gallery: {
        current_picture: itemNumber,
        galleryHeadline: galleryHeadline,
        number_of_pictures: totalItems,
      },
      event: "virt_path",
    };
    window.dataLayer.push(galleryConfig);
  };

  #handleSwipeGallery = (e: Event) => {
    const event = e as ChangeEvent;
    const { galleryHeadline, itemIndex, itemType, totalItemsWithAds } =
      event.detail;
    this.#pushToDataLayer({
      event: "gallery_swiped",
      gallery: {
        itemType: itemType || "not_set",
        galleryHeadline: galleryHeadline || "not_set",
        currentItem: itemIndex || "not_set",
        totalNumberOfItems: totalItemsWithAds || "not_set",
      },
    });
  };

  #handleVirtualPageviewMessagehandler = () => {
    const currentConfig = this.#defaultPageConfig();
    const messagehandlerConfig = {
      ...currentConfig,
      event: "virt_path",
    };
    window.dataLayer.push(messagehandlerConfig);
  };

  async #initialize(config: AnalyticsConfig) {
    // Enhance the configuration with clientside data
    config.content.molten_bundle_site_type = getViewport();
    config.tech.device_orientation = getOrientation();

    // Enhance the configuration with user data
    const user = document.querySelector("ws-user");
    if (user) {
      await window.customElements.whenDefined("ws-user");
      const status = await user.getStatus();
      config.login.distribution_channel = status.type ?? "not_set";
      config.login.hashed_user_id = status.hashedUserId ?? "not_set";
      config.login.product_user_status = status.trackingStatus ?? "not_set";
      config.login.registration_status = status.registrationStatus ?? "not_set";
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(config);

    this.#executeScripts();
    this.dispatchEvent(new CustomEvent(GTM_INIT_EVENT));
  }

  #bindTeaserEvents(teaser: HTMLElement) {
    // Extract dataset attributes with default empty strings for safety
    const {
      brandIdentifier = "",
      contentId = "",
      headline = "",
      paidCategory = "",
      position = "",
      positionGroupIndex = "",
      positionIndex = "",
      teaserContext = "",
      teaserContextHeadline = "",
      teaserType = "",
      type = "",
    } = teaser?.dataset as DOMStringMap;

    // Create an IntersectionObserver to track visibility
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.2) {
            const timerId = setTimeout(() => {
              const data = {
                event: "teaser_viewed",
                content: {
                  brand: brandIdentifier,
                  headline,
                  id: contentId,
                  paidCategory,
                  position,
                  positionIndex,
                  teaserContext,
                  teaserContextHeadline,
                  teaserType,
                  type,
                  zoneName:
                    positionGroupIndex === "not_set"
                      ? "not_set"
                      : `subsection-teaser-${positionGroupIndex}`,
                },
              };
              this.#pushToDataLayer(data);
            }, 2000);

            // Store the timer ID on the element
            teaser.dataset.impressionTimerId = String(timerId);
          } else {
            if (teaser.dataset.impressionTimerId) {
              clearTimeout(Number(teaser.dataset.impressionTimerId));
              delete teaser.dataset.impressionTimerId;
            }
          }
        });
      },
      { threshold: [0.2] },
    );

    observer.observe(teaser);
    teaser.dataset.impressionObserverAttached = "true";

    // Handle click event tracking for each teaser
    const teaserLink = teaser.querySelector("a");
    if (!teaserLink) return;

    const teaserClickHandler = () => {
      // Push the teaser click event to the data layer
      this.#pushToDataLayer({
        event: "teaser_clicked",
        content: {
          brand: brandIdentifier,
          headline,
          id: contentId,
          paidCategory,
          position,
          positionIndex,
          teaserContext,
          teaserContextHeadline,
          teaserType,
          type,
          zoneName:
            positionGroupIndex === "not_set"
              ? "not_set"
              : `subsection-teaser-${positionGroupIndex}`,
        },
      });
    };
    teaserLink.addEventListener("click", teaserClickHandler);
    // Store the handler on the element for cleanup
    teaser.dataset.teaserClickHandler = "attached";
    // @ts-expect-error - Adding custom property for cleanup
    teaserLink._storedTeaserClickHandler = teaserClickHandler;
  }

  #bindTeasers() {
    // Select all elements that match the attribute "data-trace-id='teaser'"
    this.#teaserElements = document.querySelectorAll<HTMLElement>(
      "[data-trace-id='teaser']",
    );

    this.#teaserElements.forEach((teaser) => {
      this.#bindTeaserEvents(teaser);
    });
  }

  #handleBreakingNewsEvent = (e: Event) => {
    const event = e as BreakingNewsEvent;
    if (event.detail.element && event.detail.element instanceof HTMLElement) {
      this.#breakingNewsElement = event.detail.element;
      this.#bindTeaserEvents(this.#breakingNewsElement);
    }
  };

  #unbindTeaserEvents(teaser: HTMLElement) {
    // Clean up the impression observer
    if (teaser.dataset.impressionObserverAttached === "true") {
      if (teaser.dataset.impressionTimerId) {
        clearTimeout(Number(teaser.dataset.impressionTimerId));
      }
      delete teaser.dataset.impressionObserverAttached;
      delete teaser.dataset.impressionTimerId;
    }

    // Remove the click event listener
    if (teaser.dataset.teaserClickHandler === "attached") {
      const teaserLink = teaser.querySelector("a")!;
      // @ts-expect-error - Accessing custom property
      const toBeRemoved = teaserLink._storedTeaserClickHandler;

      if (toBeRemoved) {
        teaserLink.removeEventListener("click", toBeRemoved);
        // @ts-expect-error - Deleting custom property
        delete teaserLink._storedTeaserClickHandler;
      }
      delete teaser.dataset.teaserClickHandler;
    }
  }

  #unbindTeasers() {
    if (!this.#teaserElements) return;
    // Clean up teaser click & impression observers
    this.#teaserElements.forEach((teaser) => {
      this.#unbindTeaserEvents(teaser);
    });
  }

  #bindBookmarkButtons() {
    const bookmarkPaywallTriggers = document.querySelectorAll<HTMLElement>(
      "ws-paywallcontrol .ws-open-dialog:has(.icon-bookmark)",
    );
    bookmarkPaywallTriggers.forEach((trigger) => {
      trigger.addEventListener("click", this.#handleForbiddenBookmarkAddEvents);
    });

    const bindSingleButton = (button: BookmarkButton) => {
      button.addEventListener(
        BOOKMARKBUTTON_TOGGLE_EVENT,
        this.#handleBookmarkButtonEvents,
      );
    };

    const bookmarkButtons = document.querySelectorAll("ws-bookmarkbutton");
    bookmarkButtons.forEach(bindSingleButton);

    const bookmarkList = document.querySelector("ws-bookmarklist");
    if (bookmarkList) {
      // Wait for the bookmark list to be loaded. If the event was fired before
      // the bookmarks must have been included in bookmarkButtons.
      // @ts-expect-error TS2769: No overload matches this call.
      bookmarkList.addEventListener(
        BOOKMARKLIST_LOADED_EVENT,
        (event: CustomEvent): void => {
          const newBookmarkButtons = (
            event.target as HTMLElement
          ).querySelectorAll("ws-bookmarkbutton");
          newBookmarkButtons.forEach(bindSingleButton);
        },
      );
    }
  }

  #bindCtaButtons = () => {
    if (this.#ctaListenerAttached) return;
    document.addEventListener("click", this.#onCtaClick, { capture: true });
    this.#ctaListenerAttached = true;
  };

  #onCtaClick = (event: Event) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    const cta = target.closest<HTMLElement>("[data-gtm-cta-button]");
    if (!cta) return;

    const pageConfig = this.#defaultPageConfig();
    const id = pageConfig.content?.id ?? "not_set";
    const headline = pageConfig.content?.headline ?? "not_set";
    const ctaName = cta.dataset.gtmCtaButton ?? "not_set";

    this.#pushToDataLayer({
      event: "cta_clicked",
      content: {
        id,
        headline,
        ctaName,
      },
    });
  };

  #handleForbiddenBookmarkAddEvents = (event: Event) => {
    const bookmarkPaywallTrigger = (event.target as HTMLElement).closest(
      "ws-paywallcontrol button:has(.icon-bookmark)",
    ) as unknown as PaywallControl;
    if (
      bookmarkPaywallTrigger &&
      bookmarkPaywallTrigger.hasAttribute("data-tracking-active")
    ) {
      this.#pushToDataLayer({
        event: "bookmark_clicked",
        bookmark_state: "forbidden",
        content: {
          id: bookmarkPaywallTrigger.dataset.contentId,
          headline: bookmarkPaywallTrigger.dataset.headline,
          type: bookmarkPaywallTrigger.dataset.targetType,
          brand: bookmarkPaywallTrigger.dataset.brandIdentifier,
          product_content_status:
            bookmarkPaywallTrigger.dataset.trackingPaidCategory,
          teaser_type: bookmarkPaywallTrigger.dataset.teaserType,
        },
      });
    } else if (bookmarkPaywallTrigger) {
      this.#pushToDataLayer({
        event: "bookmark_clicked",
        bookmark_state: "forbidden",
      });
    }
  };

  #handleBookmarkButtonEvents = (event: Event) => {
    const bookmarkbutton = (event.target as HTMLElement).closest(
      "ws-bookmarkbutton",
    );
    if (bookmarkbutton && bookmarkbutton.hasAttribute("data-tracking-active")) {
      const customEvent = event as CustomEvent;
      const contentId = customEvent.detail.contentId;
      this.#pushToDataLayer({
        event: "bookmark_clicked",
        bookmark_state: bookmarkbutton.active ? "added" : "removed",
        content: {
          id: contentId,
          headline: bookmarkbutton.dataset.headline,
          type: bookmarkbutton.dataset.targetType,
          brand: bookmarkbutton.dataset.brandIdentifier,
          product_content_status: bookmarkbutton.dataset.trackingPaidCategory,
          teaser_type: bookmarkbutton.dataset.teaserType,
        },
      });
    } else if (bookmarkbutton) {
      this.#pushToDataLayer({
        event: "bookmark_clicked",
        bookmark_state: bookmarkbutton.active ? "added" : "removed",
      });
    }
  };

  #bindOnboardingPopup() {
    const onboardingPopups = document.querySelectorAll("ws-onboarding-popup");

    onboardingPopups.forEach((onboardingPopup) => {
      onboardingPopup.addEventListener(
        ONBOARDINGPOPUP_SHOWN_EVENT,
        () => {
          this.#pushToDataLayer({
            event: "onboarding_tooltip_shown",
            tooltipName: onboardingPopup.dataset.tooltipName,
            content: {
              id: onboardingPopup.dataset.id,
              headline: onboardingPopup.dataset.headline,
              type: onboardingPopup.dataset.type,
            },
          });
        },
        { once: true },
      );
      onboardingPopup.addEventListener(
        ONBOARDINGPOPUP_CLOSED_EVENT,
        () => {
          this.#pushToDataLayer({
            event: "onboarding_tooltip_closed",
            tooltipName: onboardingPopup.dataset.tooltipName,
            content: {
              id: onboardingPopup.dataset.id,
              headline: onboardingPopup.dataset.headline,
              type: onboardingPopup.dataset.type,
            },
          });
        },
        { once: true },
      );
    });
  }

  #logoraBarrierListenerAttached = false;

  #bindLogoraBarrier = () => {
    if (this.#logoraBarrierListenerAttached) return;
    document.addEventListener("click", this.#onLogoraBarrierClick, {
      capture: true,
    });
    this.#logoraBarrierListenerAttached = true;
  };

  #onLogoraBarrierClick = (event: Event) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    const registerLink = target.closest<HTMLElement>(
      '[data-testid="logora-register-link"]',
    );
    if (registerLink) {
      this.#pushToDataLayer({
        event: "logora_barrier_register_clicked",
      });
      return;
    }

    const loginLink = target.closest<HTMLElement>(
      '[data-testid="logora-login-link"]',
    );
    if (loginLink) {
      this.#pushToDataLayer({
        event: "logora_barrier_login_clicked",
      });
    }
  };

  #paywallClickedListener = () => {
    const paywall = document.querySelector("ws-paywall");

    if (paywall) {
      document.addEventListener(PAYWALL_CLICKED_EVENT, () => {
        const p = paywall;

        window.dataLayer.push({
          event: "paywall_clicked",
          content: {
            id: p.contentId ?? "not_set",
            headline: p.headline ?? "not_set",
            type: p.trackingType ?? "not_set",
            sub_type: p.subtype ?? "not_set",
            overlay: p.isDialog,
          },
          product: p.getOfferData(),
        });
      });
    }
  };

  #paywallLoginClickedListener = () => {
    const paywalls = document.querySelectorAll("ws-paywall");

    paywalls.forEach((paywall) => {
      const link = [...paywall.querySelectorAll("a, ws-link")].find((elm) => {
        const linkUrl = new URL(
          elm.getAttribute("href")!,
          document.location.origin,
        );
        return (
          linkUrl!.pathname.startsWith("/p-user/login") ||
          linkUrl!.pathname.startsWith("/p-api/sso/login")
        );
      });

      if (link) {
        link.addEventListener("click", () => {
          const p = paywall;

          window.dataLayer.push({
            event: "paywall_login_clicked",
            content: {
              id: p.contentId ?? "not_set",
              headline: p.headline ?? "not_set",
              type: p.trackingType ?? "not_set",
              sub_type: p.subtype ?? "not_set",
              overlay: p.isDialog,
            },
          });
        });
      }
    });
  };

  async #handlePaywallShown() {
    await wsPaywallReady;

    const paywalls = document.querySelectorAll("ws-paywall");

    await Promise.all(
      [...paywalls].map(async (paywall) => {
        const dialog = paywall.closest("dialog");
        if (!dialog) return;

        const callback = (mutationsList: MutationRecord[]) => {
          mutationsList.forEach((mutation) => {
            if (
              mutation.type === "attributes" &&
              mutation.attributeName === "open"
            ) {
              if (dialog.hasAttribute("open")) {
                const p = paywall;
                window.dataLayer.push({
                  event: "paywall_shown",
                  content: {
                    id: p.contentId ?? "not_set",
                    headline: p.headline ?? "not_set",
                    type: p.trackingType ?? "not_set",
                    sub_type: p.subtype ?? "not_set",
                    overlay: p.isDialog,
                  },
                });
              }
            }
          });
        };

        const observer = new MutationObserver(callback);

        observer.observe(dialog, { attributes: true });
      }),
    );
  }

  async #bindPaywall() {
    await Promise.all([wsPaywallReady, wsLinkReady]);
    document.addEventListener(
      ZEPHR_DECISIONS_FINISHED_EVENT,
      this.#paywallClickedListener,
    );
    document.addEventListener(
      ZEPHR_DECISIONS_FINISHED_EVENT,
      this.#paywallLoginClickedListener,
    );
    document.addEventListener(
      ZEPHR_DECISIONS_FINISHED_EVENT,
      this.#handleInlinePaywallVisibility,
    );
    this.#handlePaywallShown();
  }

  #handleZephrOutcome() {
    document.addEventListener(
      ZEPHR_DECISIONS_FINISHED_EVENT,
      this.#zephrOutcome,
    );
  }

  #zephrOutcome = (e: any) => {
    if (e) {
      const zephrOut = (window as any).Zephr;
      const zephrOutObj = {
        zephr: {
          ...zephrOut,
        },
      };

      window.dataLayer.push(zephrOutObj);
      localStorage.setItem("zephr-out-obj", JSON.stringify(zephrOutObj));
    }
  };

  // Private method to push data to the data layer
  #pushToDataLayer(data: EventData): void {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
  }

  #handleInlinePaywallVisibility() {
    const paywall = [...document.querySelectorAll("ws-paywall")].find(
      (element) => !element.isDialog,
    );

    if (!paywall) {
      return;
    }

    function isElementAtLeast50PercentVisible(element: HTMLElement): boolean {
      const rect = element.getBoundingClientRect();
      const elementHeight = rect.height;

      if (elementHeight === 0) {
        return false;
      }

      const visibleHeight =
        Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);

      return visibleHeight / elementHeight >= 0.5;
    }

    const observeElementVisibility = (
      element: HTMLElement,
      callback: (isVisible: boolean) => void,
    ) => {
      if (element.dataset.observerAttached === "true") {
        return () => {
          /* no-op */
        };
      }

      element.dataset.observerAttached = "true";

      const checkVisibility = () => {
        const isVisible = isElementAtLeast50PercentVisible(element);
        callback(isVisible);
      };

      checkVisibility();

      const observer = new IntersectionObserver(
        ([entry]) => {
          callback(entry!.intersectionRatio >= 0.5);
        },
        {
          threshold: [0.5],
        },
      );

      observer.observe(element);

      return () => {
        observer.unobserve(element);
        delete element.dataset.observerAttached;
      };
    };

    observeElementVisibility(paywall, (isVisible) => {
      if (isVisible) {
        window.dataLayer.push({
          event: "paywall_shown",
          content: {
            id: paywall.contentId ?? "not_set",
            headline: paywall.headline ?? "not_set",
            type: paywall.trackingType ?? "not_set",
            sub_type: paywall.subtype ?? "not_set",
            overlay: false,
          },
        });
      }
    });
  }

  disconnectedCallback() {
    const galleries = document.querySelectorAll(
      "ws-gallery, ws-gallery-embed, ws-gallery-index, ws-gallery-lead",
    );
    galleries.forEach((gallery) => {
      gallery.removeEventListener(
        GALLERY_CHANGE_EVENT,
        this.#handleVirtualPageviewGallery,
      );
      gallery.removeEventListener(
        GALLERY_CHANGE_EVENT,
        this.#handleSwipeGallery,
      );
    });

    const messagehandlers = document.querySelectorAll("ws-messagehandler");
    messagehandlers.forEach((messagehandler) => {
      messagehandler.removeEventListener(
        MESSAGE_HANDLER_INTERACTION_EVENT,
        this.#handleVirtualPageviewMessagehandler,
      );
    });

    const ttsHandlers = document.querySelectorAll("ws-texttospeech");
    ttsHandlers.forEach((ttsHandler) => {
      ttsHandler.removeEventListener(
        TEXTTOSPEECH_OPENED_EVENT,
        this.#handleTtsOpened,
      );
      ttsHandler.removeEventListener(
        TEXTTOSPEECH_CLOSED_EVENT,
        this.#handleTtsClosed,
      );
    });

    const bookmarkPaywallTriggers = document.querySelectorAll<HTMLElement>(
      "ws-paywallcontrol .feature-bar__button:has(.icon-bookmark)",
    );
    bookmarkPaywallTriggers.forEach((trigger) => {
      trigger.removeEventListener(
        "click",
        this.#handleForbiddenBookmarkAddEvents,
      );
    });

    this.#unbindTeasers();

    const bookmarkButtons = document.querySelectorAll("ws-bookmarkbutton");
    bookmarkButtons.forEach((button) => {
      button.removeEventListener(
        BOOKMARKBUTTON_TOGGLE_EVENT,
        this.#handleBookmarkButtonEvents,
      );
    });

    // Clean up CTA listener if attached
    if (this.#ctaListenerAttached) {
      document.removeEventListener("click", this.#onCtaClick, true);
      this.#ctaListenerAttached = false;
    }

    if (this.#logoraBarrierListenerAttached) {
      document.removeEventListener("click", this.#onLogoraBarrierClick, true);
      this.#logoraBarrierListenerAttached = false;
    }

    document.removeEventListener(
      ZEPHR_DECISIONS_FINISHED_EVENT,
      this.#paywallClickedListener,
    );

    document.removeEventListener(
      ZEPHR_DECISIONS_FINISHED_EVENT,
      this.#paywallLoginClickedListener,
    );

    document.removeEventListener(
      ZEPHR_DECISIONS_FINISHED_EVENT,
      this.#handleInlinePaywallVisibility,
    );

    document.removeEventListener(
      ZEPHR_DECISIONS_FINISHED_EVENT,
      this.#zephrOutcome,
    );

    document.removeEventListener(
      BREAKING_NEWS_GTM_EVENT,
      this.#handleBreakingNewsEvent,
    );
    if (this.#breakingNewsElement) {
      this.#unbindTeaserEvents(this.#breakingNewsElement);
    }
  }
}

customElements.get("ws-gtm") ??
  customElements.define("ws-gtm", GoogleTagManager);

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface HTMLElementTagNameMap {
    "ws-gtm": GoogleTagManager;
  }
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    dataLayer: any[];
  }
}
